import { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Col, Row } from 'antd';
import { MultiStepFormProvider } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import { StepsSidebar } from 'components/new-ui/composed/multistep-form/StepsSidebar';
import FormContent from 'components/new-ui/composed/multistep-form/FormContent';
import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { getJob } from 'store/jobs/client';
import { JobResponse } from 'store/jobs/job';
import { getManifestById } from 'store/manifests/client';
import { Manifest } from 'store/manifests/types';
import { getMessageTypeByDeclaration } from 'views/declarations/utils/declaration-utils';
import { BrokerUploadProvider } from './broker-upload/BrokerUploadProvider';
import BrokerUploadHeader from './broker-upload/BrokerUploadHeader';
import BrokerUploadFeatureGuide from './broker-upload/BrokerUploadFeatureGuide';
import { getBrokerUploadStepsConfig } from './broker-upload/stepsConfig';
import { getBrokerUploadFeatureGuides } from './broker-upload/featureGuidesConfig';
import { BrokerUploadLocationState, createBrokerUploadInitialValues } from './broker-upload/types';
import { AISMessageTypes } from 'store/declarations/enums/common/declaration-types';

const BrokerInvoiceUpload: FC = () => {
    const { country, type } = useParams<{
        country: DeclarationCountry;
        type: DeclarationInternalType;
    }>();
    const location = useLocation();
    const locationState = useMemo(() => location.state as BrokerUploadLocationState | null, [location.state]);

    const [resolvedJob, setResolvedJob] = useState<JobResponse | undefined>(undefined);
    const [isJobResolved, setIsJobResolved] = useState(false);
    const [resolvedManifest, setResolvedManifest] = useState<Manifest | undefined>(undefined);
    const [isManifestResolved, setIsManifestResolved] = useState(false);

    useEffect(() => {
        if (locationState?.job) {
            setResolvedJob(locationState.job);
            setIsJobResolved(true);
        } else if (locationState?.declaration?.jobId) {
            getJob(locationState.declaration.jobId)
                .then((job) => setResolvedJob(job))
                .catch(() => {})
                .finally(() => setIsJobResolved(true));
        } else {
            setIsJobResolved(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Entering from a manifest pre-selects it, so the manifest step opens already answered.
    useEffect(() => {
        if (!locationState?.manifestId) {
            setIsManifestResolved(true);
            return;
        }

        getManifestById(locationState.manifestId)
            .then((manifest) => setResolvedManifest(manifest))
            .catch(() => {})
            .finally(() => setIsManifestResolved(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const jobLocked = !!(resolvedJob && locationState?.jobLocked);
    const manifestLocked = !!resolvedManifest;
    const formTypePreSelected = !!locationState?.declaration;
    const isDuplicating = !!locationState?.declaration;

    const initialFormType = useMemo(() => {
        if (locationState?.declaration) {
            return (getMessageTypeByDeclaration(locationState.declaration) as string) ?? '';
        }

        const isIrishImport = country === DeclarationCountry.IRELAND && type === DeclarationInternalType.IMPORT;
        if (isIrishImport) {
            return AISMessageTypes.H1;
        }

        return '';
    }, [locationState, country, type]);

    const initialValues = useMemo(
        () =>
            createBrokerUploadInitialValues({
                preSelectedJobId: resolvedJob?.id,
                preSelectedManifestId: resolvedManifest?.id,
                preSelectedFormType: initialFormType,
                preSelectedTemplateId: locationState?.declaration?.templateId ?? undefined,
            }),
        // Values must be stable once MultiStepFormProvider mounts — only compute once both resolve
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isJobResolved, isManifestResolved]
    );

    const steps = useMemo(
        () =>
            getBrokerUploadStepsConfig(country, type, {
                jobLocked,
                manifestLocked,
                formTypePreSelected,
                isDuplicating,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isJobResolved, isManifestResolved, country, type]
    );

    const featureGuides = useMemo(() => getBrokerUploadFeatureGuides(steps, country, type), [steps, country, type]);

    if (!isJobResolved || !isManifestResolved) return null;

    return (
        <BrokerUploadProvider
            country={country}
            type={type}
            locationState={locationState}
            initialJob={resolvedJob}
            initialManifest={resolvedManifest}
        >
            <MultiStepFormProvider initialValues={initialValues} steps={steps}>
                <StyledPageWrapper style={{ padding: '0 50px' }}>
                    <BrokerUploadHeader />
                    <Row style={{ marginTop: 32 }} gutter={20}>
                        <Col flex="none">
                            <StepsSidebar />
                        </Col>
                        <Col flex="auto">
                            <FormContent />
                        </Col>
                        {featureGuides.length > 0 && (
                            <Col flex="none">
                                <BrokerUploadFeatureGuide guides={featureGuides} />
                            </Col>
                        )}
                    </Row>
                </StyledPageWrapper>
            </MultiStepFormProvider>
        </BrokerUploadProvider>
    );
};

export default BrokerInvoiceUpload;
